// packages/console/src/components/FileTranscoder.tsx
//
// The production file-transcoder console, ported from
// docs/design/ui_kits/aether-live/FileTranscoder.jsx and wired to the real
// FT-2/FT-3/FT-4/FT-6a services. Every simulated value from the prototype is
// gone (R10(b)): the 500ms job tick, the random throughput walk, the sample
// title fallback, the Drop-link fault injector, and the index-based worker
// assignment are all replaced by live API/SSE data, and where a backend is not
// reachable the UI shows honest loading/empty/error states.
//
// Layout, spacing, and the display conventions (mono tabular numerals, em dash
// for absent values, derived-over-hardcoded, one signal blue, no shadows) are
// preserved from the prototype.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SessionState } from "../hooks/useSession";
import { useUploads } from "../hooks/useUploads";
import { useHardwareStream, useJobsStream, useLogsStream } from "../hooks/useTelemetry";
import { useJobsData } from "../hooks/useJobs";
import { useSourcesData } from "../hooks/useSources";
import { Graph, Panel, Read, ReadinessTag } from "./Parts";
import { Icon } from "./Icons";
import { TopBar } from "./TopBar";
import type { ConsoleMode } from "./ModeToggle";
import { TransferPanel, DropVeil } from "./TransferPanel";
import { SourcePanel } from "./panels/SourcePanel";
import { CostUsagePanel } from "./panels/CostUsagePanel";
import { JobQueue, type StagedSource } from "./panels/JobQueue";
import { EvePanel } from "./panels/EvePanel";
import { OutputProfilePanel } from "./panels/OutputProfilePanel";
import { DeliveryPanel } from "./panels/DeliveryPanel";
import { ProbeGraphGate } from "./panels/ProbeGraphGate";
import { PresetEditorModal } from "./PresetEditorModal";
import { retryJob as apiRetryJob, cancelJob, createJob, deletePreset, listPresets, type Preset } from "../api/jobs";
import { deleteSource, type Source } from "../api/sources";
import { listDeliveryTargets, type DeliveryTarget } from "../api/deliveries";
import { LADDER_STEPS, defaultProfileFor, defaultPackagingFor } from "../lib/preset";
import { isAdminRole } from "../lib/roles";
import { ApiError } from "../api/http";
import { useUrlUploads } from "../hooks/useUrlUploads";
import type { UploadView } from "../upload/engine";
import { fmtInt, fmtNum, fmtTimeOfDay } from "../lib/format";
import type { EveState } from "./panels/EvePanel";

const HIST_LEN = 60; // throughput sparkline ring buffer length
const STAGED_LS_KEY = "aether.staged.v1"; // persisted staged (ready-to-start) sources

// StagedSelection is one staged row's inline pipeline choice: the profile to
// encode with, the packaging (delivery container) to produce, and the output
// destination id ("" = local media library, no external push). Held per
// objectKey in FileTranscoder so Start reads the row's own configuration.
export interface StagedSelection {
  presetId: string | null;
  packaging: string; // a Container id ("mp4" | "hls" | ...)
  output: string; // a delivery-target id, or "" for the local media library
}

export function FileTranscoder({
  session,
  mode,
  onMode,
}: {
  session: SessionState;
  mode: ConsoleMode;
  onMode: (m: ConsoleMode) => void;
}) {
  // Workspace Owner gate: only role === "owner" may open the full/detailed EVE
  // profile internals; everyone else sees just the four restricted selectors.
  // The role rides on the session's active workspace (api/tenancy WorkspaceView).
  const isOwner = session.me?.workspace?.role === "owner";
  // Admin gate for the probe bit-distribution graph: admins AND owners may see
  // it (owner is a superset of admin here). ProbeGraphGate renders the panel
  // only for an admin/owner with a job selected; a non-admin never mounts it.
  const role = session.me?.workspace?.role;

  // ---- live streams (FT-4) ----
  const jobsStream = useJobsStream();
  // Hardware telemetry (FT-4): the same GPU status/connection the Hardware panel
  // reads, used to drive the live top-bar readiness tag.
  const hardware = useHardwareStream();
  const [bottom, setBottom] = useState<"log" | "xfer" | "probe">("log");
  const logs = useLogsStream(bottom === "xfer" ? "transfer" : "job");

  // ---- job + preset data (FT-3) ----
  const jobsData = useJobsData(jobsStream.transitionTick);

  // ---- source library (FT-3): landed uploads not yet turned into jobs ----
  const sourcesData = useSourcesData();

  // ---- selection + filter (client) ----
  const [sel, setSel] = useState<string | null>(null);
  // stagedSources are landed uploads awaiting a Start action. Decoupling upload
  // from transcode: a landing records a staged source here instead of creating
  // a job, and Start turns the selected staged source into a job. The staged key
  // is the objectKey (a landed upload has exactly one). Persisted to localStorage
  // (like from-URL uploads) so a "ready to start" row survives a refresh; the
  // Library remains the durable server-side fallback either way.
  const [staged, setStaged] = useState<StagedSource[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(STAGED_LS_KEY) || "[]");
      return Array.isArray(v) ? (v as StagedSource[]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(STAGED_LS_KEY, JSON.stringify(staged));
    } catch {
      /* storage full/unavailable: staged stays in-memory, Library is the fallback */
    }
  }, [staged]);

  // Per-row pipeline selection: one {presetId, packaging, output} per staged
  // objectKey, so each staged title configures its OWN profile / packaging /
  // output before Start. staged is the single source of truth for BOTH fresh
  // uploads and Library requeues, so an effect below seeds a default entry the
  // moment a row enters staged (from either path) and drops entries whose row
  // left, keeping this map in lockstep with staged. Start reads the row's entry
  // (via a ref, so an in-flight async start sees the value captured at click).
  const [stagedSel, setStagedSel] = useState<Record<string, StagedSelection>>({});
  const stagedSelRef = useRef<Record<string, StagedSelection>>({});
  stagedSelRef.current = stagedSel;

  // Delivery targets for the Output select. Delivery is gated OFF today, so the
  // endpoint 404s and this stays []; the Output select then shows the single
  // "Media library" default (value "" = local, no external push). When delivery
  // is enabled later, the list fills and the select offers real destinations.
  const [deliveryTargets, setDeliveryTargets] = useState<DeliveryTarget[]>([]);
  useEffect(() => {
    let alive = true;
    listDeliveryTargets()
      .then((t) => {
        if (alive) setDeliveryTargets(t);
      })
      .catch(() => {
        // A non-404 failure (delivery enabled but the tenancy proxy is down)
        // leaves the list empty: the Output select still offers Media library,
        // so the pipeline stays usable rather than blocking on a delivery outage.
        if (alive) setDeliveryTargets([]);
      });
    return () => {
      alive = false;
    };
  }, []);
  // libraryOpen mirrors the JobQueue view toggle ("queue" | "library"). The
  // toggle lives inside JobQueue; this holds the parent's view of it so a
  // re-stage from the Library can switch back to the queue.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "running" | "queued" | "done" | "failed">("all");
  const [xfer, setXfer] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  // ---- collapsible side columns (client-only view state) ----
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);

  // ---- preset editor flyout (create/edit the active profile) ----
  // editor carries the mode: "edit" opens the ACTIVE preset (a builtin forks on
  // save, a custom patches in place); "draft" opens a CLIENT-SIDE draft cloned
  // from a Standard profile that is created via POST only when the user saves,
  // so Cancel leaves no orphan preset. draftPreset holds that unsaved clone.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"draft" | "edit">("edit");
  const [draftPreset, setDraftPreset] = useState<Preset | null>(null);

  // EVE toggle (AM-11): the on state now drives the profile picker filter.
  // EVE-on shows only eve=true (managed matrix) profiles; EVE-off shows only
  // the workspace's own non-eve profiles. The toggle stays local view state.
  const [eve, setEve] = useState<EveState>({ on: false, formats: ["HLS", "DASH"], pending: false });

  // ---- profile picker filtered by the EVE toggle ----
  // The picker (and the default-selection effect, and the onLanded fallback)
  // all read the SAME filtered set so EVE-on never lets a non-eve profile
  // through and vice versa.
  const visiblePresets = useMemo(
    () => jobsData.presets.filter((p) => Boolean(p.eve) === eve.on),
    [jobsData.presets, eve.on],
  );

  // ---- active output profile for NEW uploads (per-upload preset selection) ----
  // Every upload that lands is turned into a job against THIS preset. Defaults
  // to the first VISIBLE preset (respecting the EVE filter) once the list loads.
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  useEffect(() => {
    // Re-select when the current pick falls outside the visible set (e.g. the
    // EVE toggle flipped): snap to the first visible profile, or clear when the
    // filtered set is empty (honest empty state, no fabricated selection).
    const stillVisible = activePresetId !== null && visiblePresets.some((p) => p.id === activePresetId);
    if (!stillVisible) {
      setActivePresetId(visiblePresets.length > 0 ? visiblePresets[0].id : null);
    }
  }, [activePresetId, visiblePresets]);
  const activePresetRef = useRef<string | null>(null);
  activePresetRef.current = activePresetId;

  // ---- inline per-row pipeline: profile options + default seeding ----
  // The inline Profile select offers EVERY workspace profile, grouped Standard →
  // EVE → Custom, independent of the EVE toggle: a staged row is a concrete title
  // to encode, so any real profile is a valid target. EVE profiles carry the
  // correct ladders (the managed EVE codec arrives later), so they run today as
  // ordinary encodes. The right-rail picker (EVE-filtered) stays the editor
  // surface. Ordering here is the flat fallback; JobQueue renders the optgroups.
  const inlineProfiles = useMemo(() => {
    const standard = jobsData.presets.filter((p) => p.builtin && !p.eve);
    const eveProfiles = jobsData.presets.filter((p) => p.eve);
    const custom = jobsData.presets.filter((p) => !p.builtin && !p.eve);
    return [...standard, ...eveProfiles, ...custom];
  }, [jobsData.presets]);

  // defaultSelectionFor builds a fresh row selection: the profile defaults to
  // the active profile when it is one the inline select offers, else the first
  // Standard (builtin, non-EVE) profile, else the first profile of any kind (null
  // only when none exist yet). It never DEFAULTS to an EVE profile, the user
  // opts into those from the grouped select. Packaging defaults to that preset's
  // primary container (valid for its codec); output defaults to "" (the local
  // media library). Requeued rows get a FRESH default too, so the user can run a
  // title with a new profile/packaging/output than before.
  const defaultSelectionFor = useCallback((): StagedSelection => {
    const active = inlineProfiles.find((p) => p.id === activePresetRef.current) ?? null;
    const preset = active ?? inlineProfiles.find((p) => p.builtin && !p.eve) ?? inlineProfiles[0] ?? null;
    return {
      presetId: preset ? preset.id : null,
      packaging: preset ? defaultPackagingFor(preset) : "mp4",
      output: "",
    };
  }, [inlineProfiles]);

  // Seed a default selection whenever a row enters staged (fresh upload OR
  // Library requeue), and drop entries whose row left. staged is the single
  // source of truth for both paths, so keying off it keeps the selection map in
  // lockstep with the visible rows without either path touching it directly. A
  // row that was seeded before presets loaded (presetId null) is re-seeded once
  // a real profile exists, so its Profile select is never stuck empty.
  useEffect(() => {
    setStagedSel((cur) => {
      let changed = false;
      const next: Record<string, StagedSelection> = {};
      for (const s of staged) {
        const existing = cur[s.objectKey];
        // Keep a user-configured entry (real presetId) untouched; (re)seed a
        // missing or preset-less entry once a real profile is available.
        if (existing && existing.presetId !== null) {
          next[s.objectKey] = existing;
        } else {
          next[s.objectKey] = defaultSelectionFor();
          changed = true;
        }
      }
      // Drop selections for rows that left staged (started or removed).
      if (!changed && Object.keys(next).length === Object.keys(cur).length) return cur;
      return next;
    });
  }, [staged, defaultSelectionFor]);

  // eveOnRef mirrors the EVE toggle for the async onLanded fallback, which must
  // pick a preset matching the current filter (eve vs non-eve) when the active
  // selection is momentarily null.
  const eveOnRef = useRef(false);
  eveOnRef.current = eve.on;

  // mountedRef gates the async job-creation loop below: it can run for minutes
  // (waiting for the source to be probed), so it must not reload() into an
  // unmounted tree or keep working after the component is gone.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ---- uploads (FT-2) ----
  // stageLanded records a landed SOURCE for later transcode, shared by both a
  // drag/drop upload (onLanded) and a cloud-to-cloud URL fetch (onAddUrl).
  // Upload and transcode are decoupled: a landing no longer creates a job, it
  // adds a staged source to the queue and refreshes the job + source lists. The
  // Start action turns a staged source into a job. Nothing is fabricated (R10).
  const stageLanded = useCallback(
    (objectKey: string, filename: string, sha256: string | null) => {
      setStaged((cur) => (cur.some((s) => s.objectKey === objectKey) ? cur : [...cur, { objectKey, sha256, filename }]));
      // Select the newly staged source so the right-column selectors configure it.
      setSel(objectKey);
      jobsData.reload();
      sourcesData.reload();
    },
    [jobsData, sourcesData],
  );

  // startJob turns a staged source into a job against THAT ROW's inline pipeline
  // selection (profile / packaging / output), captured at click, not at landing.
  // FT-3 probes and registers the source asynchronously, so POST /v1/jobs may 422
  // ("source not probed yet") briefly; retry that plus transient (network / 5xx)
  // errors with backoff until the source is probed or the deadline passes. A
  // terminal failure surfaces to the user rather than dropping the job silently;
  // nothing is fabricated (R10).
  const startJob = useCallback(
    (item: StagedSource) => {
      const objectKey = item.objectKey;
      const fileLabel = item.filename;
      void (async () => {
        // Capture the row's selection at START time (click), so changing a
        // selector afterwards does not retarget an in-flight start. Fall back to
        // the active profile + no packaging override + local output when the row
        // has no entry yet (defensive; the seed effect normally fills it).
        const rowSel = stagedSelRef.current[objectKey];
        const selected = rowSel?.presetId ?? activePresetRef.current;
        const packaging = rowSel?.packaging ?? "";
        const output = rowSel?.output ?? "";
        const deadline = Date.now() + 3 * 60_000;
        let failure: string | null = null;
        let created = false;
        for (;;) {
          if (!mountedRef.current) return; // component gone: stop, no reload
          // Resolve the preset on every attempt. A brand-new workspace seeds
          // its default preset SERVER-SIDE while the source probes, so the very
          // first upload can land before any preset exists. Treat "no preset
          // yet" like "not probed yet": wait and retry within the window rather
          // than failing the start with no job and no clear reason.
          let presetId = selected;
          if (!presetId) {
            const { presets } = await listPresets().catch(() => ({ presets: [] as Preset[] }));
            // Prefer a non-EVE preset for the fallback (the inline pipeline runs
            // concrete workspace profiles); fall back to any preset last.
            presetId = presets.find((p) => !p.eve)?.id ?? presets[0]?.id ?? null;
          }
          if (!presetId) {
            if (Date.now() < deadline) {
              await new Promise((r) => setTimeout(r, 2000));
              continue;
            }
            failure = "no output profile is available yet";
            break;
          }
          try {
            // formats carries the Packaging override (empty = use the preset's
            // formats); destinationIds carries the Output selection (empty = the
            // local media library, no external push).
            await createJob({
              objectKey,
              presetId,
              formats: packaging ? [packaging] : [],
              destinationIds: output ? [output] : [],
            });
            created = true;
            break; // created
          } catch (err) {
            // Retry within the window on "not probed yet" (422) and transient
            // errors (network reject -> not an ApiError; or a 5xx). Other 4xx
            // (bad request, quota denied, forbidden) are terminal.
            const transient = !(err instanceof ApiError) || err.status === 422 || err.status >= 500;
            if (transient && Date.now() < deadline) {
              await new Promise((r) => setTimeout(r, 2000));
              continue;
            }
            failure = err instanceof ApiError ? err.message : "network error";
            break;
          }
        }
        if (!mountedRef.current) return;
        if (failure) {
          window.alert(`Could not start the transcode for ${fileLabel}: ${failure}`);
        }
        if (created) {
          // Drop the started source from the staged set; the job now owns it.
          setStaged((cur) => cur.filter((s) => s.objectKey !== objectKey));
        }
        jobsData.reload();
      })();
    },
    [jobsData],
  );

  // onLanded fires when a drag/drop upload finishes landing. useUploads invokes
  // the LATEST onLanded via a ref, so this callback's identity changing each
  // render does not re-subscribe or double-fire. The completeUpload response
  // carries the sha256 on view.sha256, so a drag/drop landing stages with its hash.
  const onLanded = useCallback(
    (view: UploadView) => {
      if (!view.objectKey) {
        jobsData.reload();
        return;
      }
      stageLanded(view.objectKey, view.file, view.sha256);
    },
    [jobsData, stageLanded],
  );

  // onAddUrl ingests a remote URL cloud-to-cloud. The upload service streams the
  // source in the BACKGROUND (async): useUrlUploads posts the URL, shows the
  // transfer in the queue, polls to completion (surviving a page refresh), and on
  // landing stages the source exactly as a drag/drop landing would. The from-URL
  // landing surfaces the sha256 from GET /v1/uploads/{id}.
  const { urlUploads, addUrl: onAddUrl, cancel: cancelUrlUpload, busy: urlBusy } = useUrlUploads((objectKey, file, sha256) =>
    stageLanded(objectKey, file, sha256),
  );
  const { uploads, add, pause, resume, cancel } = useUploads(onLanded);

  // cancelTransfer routes a transfer cancel to the right engine by id: a
  // from-URL transfer carries the `url:` prefix (server-side fetch, DELETEd via
  // the URL-uploads hook), everything else is a chunked upload owned by the
  // upload engine. This is what the Transfer panel's Cancel actually calls, so
  // BOTH kinds of in-flight transfer truly abort.
  const cancelTransfer = useCallback(
    (id: string) => {
      if (id.startsWith("url:")) cancelUrlUpload(id);
      else cancel(id);
    },
    [cancel, cancelUrlUpload],
  );

  // ---- selection defaults to the first job once loaded ----
  useEffect(() => {
    if (sel === null && jobsData.jobs.length > 0) setSel(jobsData.jobs[0].id);
  }, [sel, jobsData.jobs]);

  // ---- batch counts derive from the LOADED job list, so the Batch progress
  // panel always matches the Job queue log below. The telemetry stream aggregate
  // (agg) is a farm-wide live rate source over a different scope, so it is used
  // only for the throughput / realtime readouts, never for the batch job counts
  // (mixing the two made the panel disagree with the log). ----
  const agg = jobsStream.aggregate;
  const jobs = jobsData.jobs;
  const counts = useMemo(() => {
    const done = jobs.filter((j) => j.state === "completed").length;
    const running = jobs.filter((j) => j.state === "running").length;
    const queued = jobs.filter((j) => j.state === "queued").length;
    const failed = jobs.filter((j) => j.state === "failed").length;
    return { done, running, queued, failed };
  }, [jobs]);

  // The telemetry aggregate lingers at its last farm-wide value after the batch
  // drains, so gate both throughput readouts (and the sparkline they feed) to 0
  // when nothing is running, rather than showing a stale fps / ×.
  const farmFps = counts.running === 0 ? 0 : agg ? agg.farmFps : null;
  const realtimeX = counts.running === 0 ? 0 : agg ? agg.aggregateSpeedX : null;

  // ---- throughput sparkline ring buffer, fed at stream cadence ----
  const [hist, setHist] = useState<number[]>(() => Array(HIST_LEN).fill(0));
  const lastFpsRef = useRef(0);
  useEffect(() => {
    if (farmFps !== null) lastFpsRef.current = farmFps;
  }, [farmFps]);
  useEffect(() => {
    // Push a sample whenever the aggregate updates. This is the derived farm
    // throughput at the stream's own cadence (no random walk).
    setHist((h) => [...h.slice(1), lastFpsRef.current]);
  }, [agg]);

  const queueRunning = jobsStream.conn === "open";

  // ---- top-bar readiness tag (live GPU state) ----
  // Reads the SAME hardware telemetry the Hardware panel uses (status.gpu +
  // conn), modelling its statusLabel logic:
  //   Ready       (green), gpu === "ok"
  //   Unavailable (red), gpu is "unavailable" or "error"
  //   Degraded    (yellow), connecting/waiting: conn not open, or gpu unknown
  const readiness = useMemo<{ label: string; tone: "ok" | "warn" | "err" }>(() => {
    const gpu = hardware.status?.gpu;
    if (gpu === "unavailable" || gpu === "error") return { label: "Unavailable", tone: "err" };
    if (hardware.conn === "open" && gpu === "ok") return { label: "Ready", tone: "ok" };
    return { label: "Degraded", tone: "warn" };
  }, [hardware.status?.gpu, hardware.conn]);

  // ---- retry ----
  const onRetry = useCallback(
    (id: string) => {
      (async () => {
        try {
          await apiRetryJob(id);
          jobsData.reload();
        } catch {
          // The queue reflects the authoritative state on the next refetch.
          jobsData.reload();
        }
      })();
    },
    [jobsData],
  );

  // ---- cancel (running or queued job) ----
  const onCancel = useCallback(
    (id: string) => {
      (async () => {
        try {
          await cancelJob(id);
        } catch (e) {
          window.alert(`Could not cancel the job: ${e instanceof Error ? e.message : "request failed"}`);
        } finally {
          jobsData.reload();
        }
      })();
    },
    [jobsData],
  );

  // ---- re-stage a Library source (requeue = re-stage, NOT immediate job) ----
  // Pushes the source back into the staged set, switches the JobQueue view back
  // to the queue, and selects it so the right-column selectors configure it.
  const onRequeue = useCallback(
    (source: Source) => {
      setStaged((cur) =>
        cur.some((s) => s.objectKey === source.objectKey)
          ? cur
          : [...cur, { objectKey: source.objectKey, sha256: source.sha256, filename: source.filename }],
      );
      setLibraryOpen(false);
      setSel(source.objectKey);
    },
    [],
  );

  // ---- delete a Library source (by sha256), then refresh the Library ----
  const onDeleteSource = useCallback(
    (sha: string) => {
      (async () => {
        try {
          await deleteSource(sha);
        } catch (e) {
          window.alert(`Could not delete the source: ${e instanceof Error ? e.message : "request failed"}`);
        } finally {
          sourcesData.reload();
        }
      })();
    },
    [sourcesData],
  );

  // ---- add media (real file picker; each file starts a real FT-2 session) ----
  const fileInput = useRef<HTMLInputElement | null>(null);
  const addMedia = () => fileInput.current?.click();
  const onFilesPicked = (files: FileList | null) => {
    const arr = files ? Array.from(files) : [];
    if (arr.length === 0) return;
    add(arr);
    setBottom("xfer");
    setXfer(null);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    // Real DataTransfer names and sizes only; no sample-title fallback (R10(b)).
    if (files.length === 0) return;
    add(files);
    setBottom("xfer");
    setXfer(null);
  };

  const selectedJob = jobs.find((j) => j.id === sel) ?? null;
  // The probe graph is admin/owner-gated and needs a selected job. It lives as a
  // bottom-center tab; when it becomes ineligible (deselection / role change)
  // the tab hides and the bottom panel falls back to the job log.
  const canProbe = isAdminRole(role) && !!selectedJob;
  useEffect(() => {
    if (bottom === "probe" && !canProbe) setBottom("log");
  }, [bottom, canProbe]);

  // The active output profile drives both new uploads and the editable panel.
  const activePreset = jobsData.presets.find((p) => p.id === activePresetId) ?? null;

  // New profile = a CLIENT-SIDE DRAFT, never a server write here. It clones a
  // Standard (builtin) profile, the currently-active one when it is Standard,
  // else the first Standard, else any preset as a last resort, under a fresh
  // default name, and opens the editor in "draft" mode. The preset is created
  // via POST only when the user clicks Save in the editor; Cancel just discards
  // the draft, so New can be started and cancelled any number of times with no
  // orphan preset and no unique-name collision on the next New (the old bug).
  const onNewProfile = useCallback(() => {
    const presets = jobsData.presets;
    const active = presets.find((p) => p.id === activePresetRef.current) ?? null;
    const standards = presets.filter((p) => Boolean(p.builtin) && !p.eve);
    const base =
      (active && active.builtin && !active.eve ? active : null) ?? standards[0] ?? presets.find((p) => !p.eve) ?? null;

    const codec = base?.videoCodec ?? "h264";
    const seedFps = base?.frameRate && base.frameRate > 0 ? base.frameRate : 30;
    const seedAudio = base?.audioBitrateKbps && base.audioBitrateKbps > 0 ? base.audioBitrateKbps : 128;
    const rc = base?.rateControl ?? "crf";

    // A synthetic, unsaved Preset the editor edits locally. builtin=false so the
    // editor patches nothing and creates on Save; id/timestamps are placeholders
    // the create path strips. The full 8-rung standard ladder seeds a from-
    // scratch draft; a clone keeps its source ladder.
    const draft: Preset = {
      id: "draft",
      workspaceId: "",
      name: "New profile",
      container: base?.container ?? "mp4",
      formats: base?.formats,
      videoCodec: codec,
      rateControl: rc,
      crf: rc === "crf" ? (base?.crf && base.crf > 0 ? base.crf : 23) : 0,
      bitrateKbps: rc === "crf" ? 0 : base?.bitrateKbps && base.bitrateKbps > 0 ? base.bitrateKbps : 8000,
      maxBitrateKbps: rc === "vbr" ? base?.maxBitrateKbps ?? 0 : 0,
      gopLength: base?.gopLength ?? 48,
      frameRate: base?.frameRate ?? seedFps,
      audioBitrateKbps: base?.audioBitrateKbps,
      audioChannels: base?.audioChannels,
      audioDisabled: base?.audioDisabled,
      perRungFrameRate: base?.perRungFrameRate ?? true,
      perRungAudio: base?.perRungAudio ?? true,
      speedPreset: base?.speedPreset ?? "p4",
      keyframeSeconds: base?.keyframeSeconds ?? 2,
      segmentSeconds: base?.segmentSeconds ?? 6,
      sceneCut: base?.sceneCut ?? true,
      scaling: base?.scaling ?? "fit",
      bitDepth: base?.bitDepth ?? 8,
      vbvMaxRatio: base?.vbvMaxRatio,
      vbvBufRatio: base?.vbvBufRatio,
      audioCodec: base?.audioCodec ?? "aac",
      builtin: false,
      ladder:
        base?.ladder ??
        LADDER_STEPS.map((s) => ({
          name: s.name,
          width: s.width,
          height: s.height,
          videoCodec: codec,
          profile: defaultProfileFor(codec),
          level: "auto",
          filter: 0,
          audioCodec: "aac" as const,
          audioChannels: 2,
          frameRate: seedFps,
          audioBitrateKbps: seedAudio,
        })),
      createdAt: "",
      updatedAt: "",
    };
    setDraftPreset(draft);
    setEditorMode("draft");
    setEditorOpen(true);
  }, [jobsData.presets]);

  // Edit opens the ACTIVE profile in the editor. A builtin/Standard profile
  // forks to a new custom on Save (handled in the editor); a custom patches in
  // place. This is the entry point wired to the panel's Edit affordance.
  const onEditProfile = useCallback(() => {
    setDraftPreset(null);
    setEditorMode("edit");
    setEditorOpen(true);
  }, []);

  // onProfileSaved runs after the editor's Save. When Save created a NEW preset
  // (a draft, or a fork of a Standard profile), select it so the user lands on
  // it, the picker will surface it under the Custom tab.
  const onProfileSaved = useCallback(
    (created: Preset | null) => {
      jobsData.reload();
      if (created) setActivePresetId(created.id);
      setDraftPreset(null);
    },
    [jobsData],
  );

  // Delete the active profile. Standard (builtin) profiles cannot be deleted
  // (the picker hides Delete for them); this only runs for a custom profile.
  // The backend allows deleting a custom preset even when past jobs used it, so
  // this just calls deletePreset and refreshes; a refusal (e.g. the workspace's
  // only preset) surfaces a clear message.
  const onDeleteProfile = useCallback(() => {
    const id = activePresetRef.current;
    const base = jobsData.presets.find((p) => p.id === id) ?? null;
    if (!id || !base) return;
    if (base.builtin) return; // guarded: Standard profiles are never deletable
    if (!window.confirm(`Delete profile "${base.name}"? This cannot be undone.`)) return;
    (async () => {
      try {
        await deletePreset(id);
        setActivePresetId(null); // re-defaults to the first preset after reload
        jobsData.reload();
      } catch (e) {
        window.alert(`Could not delete profile: ${e instanceof Error ? e.message : "request failed"}`);
      }
    })();
  }, [jobsData]);

  // Chunked (drag/drop) uploads and server-side from-URL uploads share one queue.
  const allUploads = [...uploads, ...urlUploads];
  const activeUploads = allUploads.filter((u) => u.state !== "landed" && u.state !== "canceled");

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", background: "var(--bg-base)", color: "var(--fg1)" }}>
      <input ref={fileInput} type="file" multiple style={{ display: "none" }} onChange={(e) => onFilesPicked(e.target.files)} />

      {/* Shared top bar: crumb + a readiness-tag slot + the file/live mode
          toggle + the UserMenu. The mode toggle and UserMenu live in TopBar so
          the two consoles share identical chrome. */}
      <TopBar trail={[{ label: "File transcoder" }]} mode={mode} onMode={onMode} session={session}>
        {/* Live GPU readiness tag: reads the FT-4 hardware stream (the same
            source the Hardware panel binds to). Ready (green) when the GPU is
            ok, Unavailable (red) when it is unavailable/errored, Degraded
            (yellow) while connecting or waiting. */}
        <ReadinessTag tone={readiness.tone} label={readiness.label} />
      </TopBar>

      {/* body */}
      <div style={{ flex: 1, display: "flex", gap: 12, padding: 12, minHeight: 0 }}>
        {/* left: source inspector + cost & usage (collapsible) */}
        <SideColumn open={leftOpen} onToggle={() => setLeftOpen((v) => !v)} side="left" width={292} label="Source">
          <SourcePanel job={selectedJob} />
          <CostUsagePanel usage={session.usage} />
        </SideColumn>

        {/* center: batch progress + queue + log */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <Panel title="Batch progress" style={{ flex: "none" }}>
            <div style={{ display: "flex", gap: 26, marginBottom: 11 }}>
              <Read value={String(counts.done)} unit={`/ ${jobs.length}`} label="Completed" size={22} />
              <Read value={String(counts.running)} label="In flight" size={22} />
              <Read value={String(counts.queued)} label="Queued" size={22} />
              <Read value={String(counts.failed)} label="Failed" size={22} tone={counts.failed ? "var(--err)" : undefined} />
              {/* Aggregate encode throughput is the headline of this panel, so it
                  renders noticeably larger than its neighbours. It reads 0 when
                  nothing is running: farmFps comes from the farm-wide telemetry
                  aggregate, which can linger at its last value after the batch
                  drains, so gate the DISPLAY on counts.running to avoid a stale
                  non-zero fps while idle. */}
              <Read value={fmtInt(farmFps)} unit="fps" label="Throughput" size={30} />
              <Read value={fmtNum(realtimeX, 1)} unit="×" label="Realtime" size={22} />
            </div>
            <Graph data={hist} max={Math.max(900, (farmFps ?? 0) * 1.25)} live={queueRunning} />
          </Panel>

          <div
            style={{ flex: 1, minHeight: 0, display: "flex", position: "relative" }}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
            }}
            onDrop={onDrop}
          >
            <JobQueue
              jobs={jobs}
              presets={jobsData.presets}
              uploads={activeUploads}
              progress={jobsStream.progress}
              filter={filter}
              onFilter={setFilter}
              sel={sel}
              onSelJob={setSel}
              xfer={xfer}
              onSelXfer={(id) => {
                setBottom("xfer");
                setXfer(id);
              }}
              onRetry={onRetry}
              onCancel={onCancel}
              loading={jobsData.loading}
              errorMessage={jobsData.errorMessage}
              staged={staged}
              onStartJob={startJob}
              inlinePresets={inlineProfiles}
              stagedSel={stagedSel}
              onStagedSelChange={(objectKey, patch) =>
                setStagedSel((cur) => ({ ...cur, [objectKey]: { ...cur[objectKey], ...patch } }))
              }
              deliveryTargets={deliveryTargets}
              sources={sourcesData.sources}
              sourcesLoading={sourcesData.loading}
              sourcesError={sourcesData.errorMessage}
              onRequeue={onRequeue}
              onDeleteSource={onDeleteSource}
              libraryOpen={libraryOpen}
              onLibraryOpen={setLibraryOpen}
            />
            <DropVeil over={over} />
          </div>

          <Panel
            title={bottom === "xfer" ? "Transfer" : bottom === "probe" ? "Probe graph" : "Job log"}
            style={{ height: bottom === "probe" ? 320 : bottom === "xfer" ? 236 : 148, flex: "none" }}
            bodyStyle={{ padding: 0, flex: 1, minHeight: 0 }}
            right={
              <div style={{ display: "flex", gap: 6 }}>
                <TabChip active={bottom === "xfer"} onClick={() => setBottom("xfer")}>
                  Transfer{activeUploads.length ? ` · ${activeUploads.length}` : ""}
                </TabChip>
                <TabChip active={bottom === "log"} onClick={() => setBottom("log")}>
                  Job log
                </TabChip>
                {/* Admin/owner-only probe graph tab, shown only with a job selected. */}
                {canProbe && (
                  <TabChip active={bottom === "probe"} onClick={() => setBottom("probe")}>
                    Probe graph
                  </TabChip>
                )}
              </div>
            }
          >
            {bottom === "probe" ? (
              <ProbeGraphGate
                role={role}
                jobId={selectedJob?.id ?? null}
                filename={selectedJob?.sourceFilename ?? selectedJob?.sourceObjectKey?.split("/").pop()}
              />
            ) : bottom === "xfer" ? (
              <TransferPanel
                uploads={allUploads}
                sel={xfer}
                onSel={setXfer}
                pause={pause}
                resume={resume}
                cancel={cancelTransfer}
                onAddMedia={addMedia}
                onAddUrl={onAddUrl}
                urlBusy={urlBusy}
              />
            ) : (
              <LogView lines={logs.lines} conn={logs.conn} />
            )}
          </Panel>
        </div>

        {/* right: EVE + profile menu + output profile + delivery (collapsible) */}
        <SideColumn open={rightOpen} onToggle={() => setRightOpen((v) => !v)} side="right" width={312} label="Profile">
          <EvePanel state={eve} onChange={setEve} />
          {/* Profile menu: at the top of the right column, directly UNDER EVE. */}
          <ProfileMenu
            presets={visiblePresets}
            activePresetId={activePresetId}
            onChange={setActivePresetId}
            onNew={onNewProfile}
            onDelete={onDeleteProfile}
            eveOn={eve.on}
          />
          <OutputProfilePanel
            preset={activePreset}
            eve={eve}
            job={selectedJob}
            isOwner={isOwner}
            onEdit={onEditProfile}
            onReload={() => jobsData.reload()}
          />
          <DeliveryPanel live={queueRunning} />
        </SideColumn>
      </div>

      {/* The manual flyout edits non-EVE profiles only. An EVE profile's "Full
          view" (owner-gated) is the Phase 3 admin editable-command surface,
          rendered INLINE inside OutputProfilePanel; it never opens this manual
          editor. */}
      {editorOpen &&
        (() => {
          // The editor edits a client-side DRAFT (New) or the active preset
          // (Edit). Never open it for an EVE profile (its surface is inline in
          // OutputProfilePanel). The draft is always non-eve by construction.
          const source = editorMode === "draft" ? draftPreset : activePreset;
          if (!source || source.eve) return null;
          return (
            <PresetEditorModal
              preset={source}
              mode={editorMode}
              onPatched={() => jobsData.reload()}
              onSaved={onProfileSaved}
              onClose={() => {
                setEditorOpen(false);
                setDraftPreset(null);
              }}
            />
          );
        })()}
    </div>
  );
}

// SideColumn wraps a body column so it can collapse to a thin rail. Expanded, it
// shows a collapse toggle above its children; collapsed, a rail with an expand
// toggle and a vertical label. The chevron points the way it will move.
function SideColumn({
  open,
  onToggle,
  side,
  width,
  label,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  side: "left" | "right";
  width: number;
  label: string;
  children: React.ReactNode;
}) {
  const btn: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: "var(--r-xs)",
    cursor: "pointer",
    color: "var(--fg2)",
    background: "transparent",
    border: "1px solid var(--line-strong)",
  };
  if (!open) {
    return (
      <div
        style={{
          width: 34,
          flex: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          padding: "8px 0",
          background: "var(--bg-panel)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-sm)",
          minHeight: 0,
        }}
      >
        <button className="ae-b" onClick={onToggle} title={`Expand ${label}`} style={btn}>
          <Icon name="chevron-right" size={14} style={{ transform: side === "right" ? "rotate(180deg)" : "none" }} />
        </button>
        <div
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            font: "600 9px var(--font-sans)",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--fg3)",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
      </div>
    );
  }
  return (
    <div style={{ width, flex: "none", display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
      <div style={{ flex: "none", display: "flex", justifyContent: side === "left" ? "flex-end" : "flex-start" }}>
        <button className="ae-b" onClick={onToggle} title={`Collapse ${label}`} style={btn}>
          <Icon name="chevron-right" size={14} style={{ transform: side === "left" ? "rotate(180deg)" : "none" }} />
        </button>
      </div>
      {children}
    </div>
  );
}

// ProfileMenu is the output-profile selector plus New/Delete, moved out of the
// crowded top bar into the right column directly under EVE. It selects the preset
// applied to new uploads; the OutputProfilePanel below edits the selected one.
function ProfileMenu({
  presets,
  activePresetId,
  onChange,
  onNew,
  onDelete,
  eveOn,
}: {
  presets: Preset[];
  activePresetId: string | null;
  onChange: (id: string | null) => void;
  onNew: () => void;
  onDelete: () => void;
  eveOn: boolean;
}) {
  const act: React.CSSProperties = {
    flex: 1,
    minHeight: 34,
    borderRadius: "var(--r-sm)",
    font: "600 10px var(--font-sans)",
    letterSpacing: ".09em",
    textTransform: "uppercase",
    background: "transparent",
    border: "1px solid var(--line-strong)",
  };

  const standard = presets.filter((p) => Boolean(p.builtin));
  const custom = presets.filter((p) => !p.builtin);

  // The non-EVE picker is a TWO-TAB interface: "Standard" (builtin) and
  // "Custom" (user-authored). Both tabs are always available. The tab defaults
  // to the one holding the active profile, and re-snaps when the active profile
  // moves to the other tab (e.g. a New profile saves into Custom and is
  // selected). Standard profiles cannot be deleted, so Delete is shown only on
  // the Custom tab.
  const activeInCustom = custom.some((p) => p.id === activePresetId);
  const [tab, setTab] = useState<"standard" | "custom">(activeInCustom ? "custom" : "standard");
  const activeTabRef = useRef<"standard" | "custom" | null>(null);
  const desiredTab = activeInCustom ? "custom" : "standard";
  useEffect(() => {
    // Follow the active profile to its tab, but only when the active selection
    // actually changed tab (not on every render), so a user browsing the other
    // tab is not yanked back while the active profile stays put.
    if (activeTabRef.current !== desiredTab) {
      activeTabRef.current = desiredTab;
      setTab(desiredTab);
    }
  }, [desiredTab]);

  const tabList = tab === "standard" ? standard : custom;

  // EVE profiles are the managed delivery×codec matrix: they are picked, not
  // cloned or deleted here, so New/Delete are hidden while EVE is on. With EVE
  // off the two tabs split the non-EVE profiles: "Standard" (builtin) and
  // "Custom" (user-authored). The active profile is clearly highlighted (filled
  // + bordered) so it is obvious which is in use.
  const renderOption = (p: Preset) => {
    const active = p.id === activePresetId;
    return (
      <button
        key={p.id}
        className="ae-b"
        role="option"
        aria-selected={active}
        onClick={() => onChange(p.id)}
        title={p.name}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          minHeight: 34,
          padding: "0 10px",
          borderRadius: "var(--r-sm)",
          cursor: "pointer",
          textAlign: "left",
          font: "400 12px var(--font-mono)",
          color: active ? "#fff" : "var(--fg2)",
          background: active ? "var(--blue-tint)" : "transparent",
          border: `1px solid ${active ? "var(--blue-500)" : "var(--line-strong)"}`,
        }}
      >
        {active && <Icon name="check" size={13} color="var(--blue-300)" />}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.name}
        </span>
      </button>
    );
  };

  // A tab button in the Standard/Custom selector.
  const tabBtn = (id: "standard" | "custom", label: string, count: number) => {
    const on = tab === id;
    return (
      <button
        key={id}
        className="ae-b"
        onClick={() => setTab(id)}
        style={{
          flex: 1,
          minHeight: 28,
          borderRadius: "var(--r-xs)",
          cursor: "pointer",
          font: "600 10px var(--font-sans)",
          letterSpacing: ".09em",
          textTransform: "uppercase",
          background: on ? "var(--blue-tint)" : "transparent",
          border: `1px solid ${on ? "var(--blue-500)" : "var(--line-strong)"}`,
          color: on ? "var(--blue-300)" : "var(--fg2)",
        }}
      >
        {label}
        {count > 0 ? ` · ${count}` : ""}
      </button>
    );
  };

  // Delete is enabled only on the Custom tab, for a selected custom profile.
  // Standard profiles can't be deleted, so Delete is hidden on the Standard tab.
  const activePresetInTab = tabList.some((p) => p.id === activePresetId);
  const canDelete = tab === "custom" && activePresetInTab;

  return (
    <Panel title={eveOn ? "EVE profiles" : "Output profiles"} style={{ flex: "none" }}>
      {eveOn ? (
        presets.length === 0 ? (
          <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>No EVE profiles yet for this workspace.</div>
        ) : (
          <div
            role="listbox"
            aria-label="EVE profile applied to new uploads"
            style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 176, overflowY: "auto" }}
          >
            {presets.map(renderOption)}
          </div>
        )
      ) : (
        <>
          {/* Two always-available tabs. The active profile lives in one of them
              and is highlighted there; the picker defaults to that tab. */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {tabBtn("standard", "Standard", standard.length)}
            {tabBtn("custom", "Custom", custom.length)}
          </div>
          <div
            role="listbox"
            aria-label="Output profile applied to new uploads"
            style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 264, overflowY: "auto" }}
          >
            {tabList.length ? (
              tabList.map(renderOption)
            ) : (
              <div style={{ font: "var(--t-micro)", color: "var(--fg4)", padding: "2px" }}>
                {tab === "standard" ? "No standard profiles." : "No custom profiles yet. Use New to create one."}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              className="ae-b"
              onClick={onNew}
              title="Create a new custom profile (from a Standard profile)"
              style={{ ...act, color: "var(--fg1)", cursor: "pointer" }}
            >
              <Icon name="plus" size={11} /> New
            </button>
            {/* Standard profiles can't be deleted, so Delete only shows on the
                Custom tab; disabled until a custom profile is selected. */}
            {tab === "custom" && (
              <button
                className="ae-b"
                onClick={onDelete}
                disabled={!canDelete}
                title={canDelete ? "Delete the active custom profile" : "Select a custom profile to delete"}
                style={{
                  ...act,
                  color: canDelete ? "var(--fg1)" : "var(--fg4)",
                  cursor: canDelete ? "pointer" : "not-allowed",
                }}
              >
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}

function TabChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      className="ae-b"
      onClick={onClick}
      style={{
        font: "600 10px var(--font-sans)",
        letterSpacing: ".09em",
        textTransform: "uppercase",
        padding: "6px 10px",
        borderRadius: "var(--r-xs)",
        cursor: "pointer",
        whiteSpace: "nowrap",
        background: active ? "var(--blue-tint)" : "transparent",
        border: `1px solid ${active ? "var(--blue-500)" : "var(--line-strong)"}`,
        color: active ? "var(--blue-300)" : "var(--fg2)",
      }}
    >
      {children}
    </button>
  );
}

function LogView({ lines, conn }: { lines: { line: string; tag: string; level: string; at: string }[]; conn: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);
  if (lines.length === 0) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--fg4)", font: "var(--t-micro)" }}>
        {conn === "open" ? "No log lines yet" : conn === "error" ? "Log stream unavailable" : "Connecting to log stream..."}
      </div>
    );
  }
  return (
    <div ref={ref} style={{ height: "100%", overflowY: "auto", padding: "8px 13px", display: "flex", flexDirection: "column", gap: 3 }}>
      {lines.map((l, i) => (
        <div key={i} style={{ display: "flex", gap: 10, font: "400 11px var(--font-mono)", color: "var(--fg2)", whiteSpace: "nowrap" }}>
          <span style={{ color: "var(--fg4)" }}>{fmtTimeOfDay(l.at)}</span>
          <span style={{ color: l.level === "warn" || l.level === "error" ? "var(--warn)" : "var(--blue-400)", width: 30 }}>{l.tag}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{l.line}</span>
        </div>
      ))}
    </div>
  );
}
